import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type TextStyle,
} from 'react-native';

import { useReducedMotion } from '../useReducedMotion';

// One line of text that scrolls when it does not fit (#308, the logger card
// title). It stays still when it fits. When it does not: a pause at the start,
// a slide to the end, a pause, a slide back, THREE rounds, then it rests at the
// start. A text that moves for the whole rest distracts (Antenore, v3).
// Reduce motion: no scroll, the text wraps to two lines instead.
// The full text is always the caller's accessible name; this view is visual.

const ROUNDS = 3;
const PAUSE_MS = 1200;
const PX_PER_SEC = 30;
// Wide enough that the measuring row never wraps the text it measures.
const MEASURE_WIDTH = 4000;

export function MarqueeText({
  text,
  style,
}: {
  text: string;
  style?: StyleProp<TextStyle>;
}) {
  const reducedMotion = useReducedMotion();
  const [boxW, setBoxW] = useState(0);
  const [textW, setTextW] = useState(0);
  const [running, setRunning] = useState(false);
  const shift = useRef(new Animated.Value(0)).current;
  const overflow = Math.max(0, Math.ceil(textW - boxW));

  useEffect(() => {
    shift.setValue(0);
    if (reducedMotion || overflow === 0 || boxW === 0) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const wait = (ms: number) =>
      new Promise<void>((res) => timers.push(setTimeout(res, ms)));
    const slide = (toValue: number) =>
      new Promise<boolean>((res) =>
        Animated.timing(shift, {
          toValue,
          duration: (overflow / PX_PER_SEC) * 1000,
          // RNW has no native driver and warns when asked for one.
          useNativeDriver: Platform.OS !== 'web',
        }).start(({ finished }) => res(finished)),
      );
    // `running` is true only while the track is away from the start, so the
    // ellipsis shows through the opening pause and after every round.
    (async () => {
      for (let round = 0; round < ROUNDS; round += 1) {
        await wait(PAUSE_MS);
        if (cancelled) return;
        setRunning(true);
        if (!(await slide(-overflow))) return;
        await wait(PAUSE_MS);
        if (cancelled) return;
        if (!(await slide(0))) return;
        setRunning(false);
      }
    })();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      shift.stopAnimation();
      setRunning(false);
    };
  }, [text, overflow, boxW, reducedMotion, shift]);

  if (reducedMotion) {
    return (
      <Text style={[styles.wrap, style]} numberOfLines={2}>
        {text}
      </Text>
    );
  }

  // At rest a text that does not fit ends in an ellipsis, never in half a
  // letter. The measuring row (textW) is kept mounted, so the sliding track
  // comes back as soon as a round starts.
  const clipped = overflow > 0 && !running;

  return (
    <View
      style={styles.box}
      onLayout={(e: LayoutChangeEvent) => setBoxW(e.nativeEvent.layout.width)}
    >
      <Animated.View
        style={[styles.track, { transform: [{ translateX: shift }] }]}
        // Visual only: the caller's accessible name carries the full text.
        importantForAccessibility="no-hide-descendants"
        accessibilityElementsHidden
        aria-hidden
      >
        <Text
          style={[style, clipped && styles.measure]}
          numberOfLines={1}
          onLayout={(e: LayoutChangeEvent) =>
            setTextW(e.nativeEvent.layout.width)
          }
        >
          {text}
        </Text>
      </Animated.View>
      {clipped ? (
        <Text style={[style, styles.rest]} numberOfLines={1} ellipsizeMode="tail">
          {text}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  box: { flex: 1, minWidth: 0, overflow: 'hidden' },
  wrap: { flex: 1, minWidth: 0 },
  measure: { opacity: 0 },
  rest: { position: 'absolute', left: 0, right: 0 },
  track: { flexDirection: 'row', width: MEASURE_WIDTH },
});
